import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography, Grid, Paper } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import Modal from "@mui/material/Modal";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useFormikContext } from "formik";
import "./style.css";
import { fetchCalendarTimeSlots } from "../Services/Organisation.service";
import { useTranslation } from "react-i18next";
import * as es from "date-fns/locale/es";
//import * as en from "date-fns/locale/en";
import { Locale } from "date-fns";
import moment from 'moment-timezone';

registerLocale("es", (es as any).default as Locale);

interface TimeSlotPickerProps {
  timeSlots: string[];
  onTimeSlotSelect: (timeSlot: string) => void;
  name: string;
  label: string;
  popup?: boolean;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  name,
  label,
  timeSlots,
  onTimeSlotSelect,
  popup = false,
}) => {
  const { t } = useTranslation();
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 34,
    borderRadius: 10,
    p: 4,
  };
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  return (
    <Box className="timeslotwrapper">
      <Typography variant="h6" >{t("Time Slot")}</Typography>
      <Grid container spacing={2}>
        {timeSlots.map((slot: any) => {
          // console.log(moment(slot?.timeslottimestart).utc().format("hh:mm A"), moment().utc().format("hh:mm A"))
          const utcSlotStart = moment(slot?.timeslottimestart).utc();
          const currentUtc = moment.utc();
          const curacaoSlotStart = utcSlotStart.clone().tz("America/Curacao");
          const curacaoCurrentTime = currentUtc.clone().tz("America/Curacao");
          const diffInMinutes = curacaoSlotStart.diff(curacaoCurrentTime, 'minutes');
          return (
            <Grid item key={slot.id}>
              <Button
                className="time-slot-button"
                onClick={() => {
                  if (slot?.timeslotcapacity && slot?.timeslotcapacity > 0) {
                    onTimeSlotSelect(slot);
                  }
                  if (popup == true && slot?.timeslotcapacity === 0) {
                    handleOpen();
                  }
                }}
                disabled={popup ? false : slot?.timeslotcapacity <= 0}
              >
                <Box display={"flex"} flexDirection={"column"} padding={"0.5rem 1rem"} margin={0}>
                  <Typography className={"timeslottext"} variant="caption">
                    {moment(slot?.timeslottimestart).utc().format("hh:mm A")} - {moment(slot?.timeslotend).utc().format("hh:mm A")}
                  </Typography>
                  {/* <Typography style={{ fontSize: "12px", fontWeight: "lighter", padding: "5px 0" }}>
                      {slot?.timeslotcapacity > 0 && "available"}
                      {slot?.timeslotcapacity <= 0 && "Sold Out"}
                    </Typography> */}
                </Box>
              </Button>
            </Grid>
          )
        }
        )}
      </Grid>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Opps sold out
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            Right now all the slots are booked. But you can contact in person, we can fingure out
            something
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

interface DateTimeSlotPickerProps {
  dateName: string;
  dateLabel: string;
  timeName: string;
  timeLabel: string;
  setActiveStep: (data: any) => void;
  calendarid: string;
  popup?: boolean;
}

const DateTimeSlotPicker: React.FC<DateTimeSlotPickerProps> = ({
  dateName,
  dateLabel,
  timeName,
  timeLabel,
  setActiveStep,
  calendarid,
  popup = false,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [monthBody, setMonthBody] = useState<Object | any>(null);
  const [availableDates, setAvailableDates] = useState<any[]>([]);
  const { values, setFieldValue } = useFormikContext();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  
  useEffect(() => {
    currentMonthBody();
  }, []);

  useEffect(() => {
    if (calendarid && monthBody) {
      fetchTimeSlot();
    }
  }, [calendarid, monthBody]);

  const fetchTimeSlot = async () => {
    try {
      const body = monthBody;
      const response = await fetchCalendarTimeSlots(calendarid, body);
      if (response) {
        setAvailableDates(response.list);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const currentMonthBody = () => {
    const date = new Date();
    handleMonthChange(date);
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    const formattedDate = formatDate(date as Date);
    setFieldValue("desiredDate", formattedDate);
    setSelectedTimeSlot(null);
  };

  const handleTimeSlotSelect = (timeSlot: any) => {
    setSelectedTimeSlot(timeSlot);
    const timeslotString = timeSlot?.timeslottimestart;
    setFieldValue("desiredTime", timeslotString);
    setFieldValue("desiredEndTime", moment(timeSlot?.timeslotend).utc().format("hh:mm A"));
    setFieldValue("timeslotid", timeSlot.timeslotid);
    setFieldValue("selectedTimeSlotCapacity", timeSlot.timeslotcapacity);
    setFieldValue("calendarid", timeSlot.calendarid);
    timeSlot && setActiveStep(1);
  };

  const formatDate = (date: Date) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
      .getDate()
      .toString()
      .padStart(2, "0")}`;
  };
  
  const handleMonthChange = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 2, 0);

    setMonthBody({
      startdate: formatDate(startDate),
      enddate: formatDate(endDate),
    });
  };

  const activeDates = useMemo(() => {
    const activeList = availableDates
      .filter((slot) => slot.timeslotactive)
      .map((slot) => new Date(slot.timeslotdate));
    return activeList;
  }, [availableDates]);

  const isDateAvailable = (date: Date) => {
    return activeDates.some(
      (activeDate) =>
        date.getDate() === activeDate.getDate() &&
        date.getMonth() === activeDate.getMonth() &&
        date.getFullYear() === activeDate.getFullYear()
    );
  };

  const getTimeSlotsForDate = (date: Date | null): string[] => {
    if (!date) return [];
    return availableDates?.filter(
      (obj: any) => new Date(obj?.timeslotdate).toDateString() === date.toDateString()
    );
  };

  const dayClassName = (date: any) => {
    return isDateAvailable(date) ? 'highlight-datessssss' : '';
  };

  // Memoize availableTimeSlots to prevent recalculation on every render
  const availableTimeSlots = useMemo(() => {
    return getTimeSlotsForDate(selectedDate);
  }, [selectedDate, availableDates]);

  // Auto-select time slot if there's only one available for the selected date
  useEffect(() => {
    if (selectedDate && availableTimeSlots.length === 1 && !selectedTimeSlot) {
      const singleTimeSlot: any = availableTimeSlots[0];
      // Only auto-select if the slot has capacity
      if (singleTimeSlot?.timeslotcapacity > 0) {
        handleTimeSlotSelect(singleTimeSlot);
      }
    }
  }, [selectedDate, availableTimeSlots.length, selectedTimeSlot]); // Only depend on length, not the entire array

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2 }} className="datetimecontainer">
        <>
          <Typography variant="h6"> {t("SelectDate")}</Typography>
        </>
        <DatePicker
          inline
          selected={selectedDate}
          onChange={(date) => handleDateChange(date)}
          monthsShown={window.innerWidth > 767 ? 2 : 1}
          onMonthChange={handleMonthChange}
          minDate={new Date()}
          filterDate={isDateAvailable}
          dayClassName={dayClassName}
          name={dateName}
          {...(currentLanguage === "fr" && { locale: "es" })}
        />

        {selectedDate && availableTimeSlots.length > 1 && (
          <TimeSlotPicker
            name={timeName}
            label={timeLabel}
            timeSlots={availableTimeSlots}
            popup={popup}
            onTimeSlotSelect={handleTimeSlotSelect}
          />
        )}
        
        {/* Show a message when there's only one slot and it's auto-selected */}
        {selectedDate && availableTimeSlots.length === 1 && selectedTimeSlot && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body1" color="success.dark">
              {t("Automatically selected the only available time slot")}
            </Typography>
          </Box>
        )}

        {/* Show message when there's only one slot but it has no capacity */}
        {selectedDate && availableTimeSlots.length === 1 && (availableTimeSlots[0] as any)?.timeslotcapacity <= 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body1" color="error.dark">
              {t("The only available time slot is fully booked")}
            </Typography>
          </Box>
        )}

        {selectedTimeSlot && (
          <Typography variant="h6">
            {t("Selected Time Slot")}: {selectedDate?.toString()}, {selectedTimeSlot}
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DateTimeSlotPicker;